import { FormArray, FormControl, Validators } from '@angular/forms';

import { WithPrestationsAndFilesBaseForm } from '@bases/with-prestations-and-files-form/with-prestations-and-files-form.base';
import { GIFileForm } from '@forms/gi-file-form';
import { ContractStatus } from '@gi/enums/contract-status.enum';
import { ContractAnnexForm } from '@gi/forms/contract-annex-form';
import { PrestationForm } from '@gi/forms/prestation-form';
import { Contract } from '@gi/models/contract';
import { ContractAnnex } from '@gi/models/contract-annex';
import { ContractOffer } from '@gi/models/contract-offer';
import { Prospect } from '@gi/models/prospect';
import { DateHelper } from '@helpers/date.helper';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';
import { BusinessUnit } from '@models/business-unit';
import { BusinessUser } from '@models/business-user';
import { Company } from '@models/company';
import { Contributor } from '@models/contributor';
import { GIFile } from '@models/gi-file';
import { ModelOffer } from '@models/model-offer';
import { ModelOfferContractAnnexModel } from '@models/model-offer-contract-annex-model';
import { User } from '@models/user';

export type ContractFormType = {
  id: FormControl<number | undefined>;
  startDate: FormControl<string | null>;
  internalComment: FormControl<string | null>;
  contractStatus: FormControl<ContractStatus | null>;
  company: FormControl<Company | null>;
  businessUnit: FormControl<BusinessUnit | null>;
  owner: FormControl<Contributor | null>;
  contact: FormControl<User | null>;
  prospect: FormControl<Prospect | null>;
  sentAt: FormControl<Date | null>;
  signedAt: FormControl<Date | null>;
  lostAt: FormControl<Date | null>;
  sentBy: FormControl<BusinessUser | null>;
  signedBy: FormControl<User | null>;
  lostBy: FormControl<BusinessUser | null>;
  lossReason: FormControl<string | null>;
  lossComment: FormControl<string | null>;
  archivedAt: FormControl<Date | null>;
  archivedBy: FormControl<BusinessUser | null>;
  statusAt: FormControl<Date | null>;
  statusBy: FormControl<BusinessUser | null>;
  reminderCount: FormControl<number | null>;
  nextReminderDate: FormControl<string | null>;
  brand: FormControl<string | null>;
  logoFileId: FormControl<string | null>;
  annexes: FormArray<ContractAnnexForm>;
  isLastSignatureExpired: FormControl<boolean | null>;
  nextReminderDateModifiedAt: FormControl<Date | null>;
  nextReminderDateModifiedBy: FormControl<BusinessUser | null>;
};

export class ContractForm extends WithPrestationsAndFilesBaseForm<ContractFormType> {
  get id(): FormControl<number | undefined> {
    return this.controls.id;
  }

  get startDate(): FormControl<string | null> {
    return this.controls.startDate;
  }

  get startDateErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.startDate, 'La date de début du contrat est requise');
  }

  get internalComment(): FormControl<string | null> {
    return this.controls.internalComment;
  }

  get contractStatus(): FormControl<ContractStatus | null> {
    return this.controls.contractStatus;
  }

  get prestationsErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.prestations, 'Veuillez ajouter au minimum une prestation');
  }

  get company(): FormControl<Company | null> {
    return this.controls.company;
  }

  get companyErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.company, 'La source du contrat (dossier) est requise');
  }

  get businessUnit(): FormControl<BusinessUnit | null> {
    return this.controls.businessUnit;
  }

  get businessUnitErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.businessUnit, 'Groupe requis');
  }

  get owner(): FormControl<Contributor | null> {
    return this.controls.owner;
  }

  get ownerErrorMessage(): ErrorMessage | null {
    return FormHelper.getEmptyErrorMessage(this.owner, 'Le référent commercial du contrat est requis');
  }

  get contact(): FormControl<User | null> {
    return this.controls.contact;
  }

  get contactErrorMessage(): ErrorMessage | null {
    return FormHelper.composeErrorMessages(this.contact, [
      {
        method: 'simpleErrorMessage',
        args: [
          {
            simpleErrorCode: 'incompleteContact',
            simpleErrorMessage: 'Le numéro de téléphone et le mail sont requis'
          }
        ]
      },
      { method: 'getEmptyErrorMessage', args: ['Le contact du contrat est requis'] }
    ]);
  }

  get prospect(): FormControl<Prospect | null> {
    return this.controls.prospect;
  }

  get sentAt(): FormControl<Date | null> {
    return this.controls.sentAt;
  }

  get signedAt(): FormControl<Date | null> {
    return this.controls.signedAt;
  }

  get lostAt(): FormControl<Date | null> {
    return this.controls.lostAt;
  }

  get sentBy(): FormControl<BusinessUser | null> {
    return this.controls.sentBy;
  }

  get signedBy(): FormControl<User | null> {
    return this.controls.signedBy;
  }

  get lostBy(): FormControl<BusinessUser | null> {
    return this.controls.lostBy;
  }

  get lossReason(): FormControl<string | null> {
    return this.controls.lossReason;
  }

  get lossComment(): FormControl<string | null> {
    return this.controls.lossComment;
  }

  get archivedAt(): FormControl<Date | null> {
    return this.controls.archivedAt;
  }

  get archivedBy(): FormControl<BusinessUser | null> {
    return this.controls.archivedBy;
  }

  get statusAt(): FormControl<Date | null> {
    return this.controls.statusAt;
  }

  get statusBy(): FormControl<BusinessUser | null> {
    return this.controls.statusBy;
  }

  get reminderCount(): FormControl<number | null> {
    return this.controls.reminderCount;
  }

  get nextReminderDate(): FormControl<string | null> {
    return this.controls.nextReminderDate;
  }

  get nextReminderDateModifiedAt(): FormControl<Date | null> {
    return this.controls.nextReminderDateModifiedAt;
  }

  get nextReminderDateModifiedBy(): FormControl<BusinessUser | null> {
    return this.controls.nextReminderDateModifiedBy;
  }

  get brand(): FormControl<string | null> {
    return this.controls.brand;
  }

  get logoFileId(): FormControl<string | null> {
    return this.controls.logoFileId;
  }

  get annexes(): FormArray<ContractAnnexForm> {
    return this.controls.annexes;
  }

  get isLastSignatureExpired(): FormControl<boolean | null> {
    return this.controls.isLastSignatureExpired;
  }

  constructor() {
    super({
      id: new FormControl<number | undefined>(undefined),
      startDate: new FormControl<string | null>(null),
      internalComment: new FormControl<string | null>(null),
      contractStatus: new FormControl<ContractStatus | null>(null, Validators.required),
      company: new FormControl<Company | null>(null),
      businessUnit: new FormControl<BusinessUnit | null>(null, Validators.required),
      owner: new FormControl<Contributor | null>(null),
      contact: new FormControl<User | null>(null),
      prospect: new FormControl<Prospect | null>(null),
      sentAt: new FormControl<Date | null>(null),
      signedAt: new FormControl<Date | null>(null),
      lostAt: new FormControl<Date | null>(null),
      sentBy: new FormControl<BusinessUser | null>(null),
      signedBy: new FormControl<User | null>(null),
      lostBy: new FormControl<BusinessUser | null>(null),
      lossReason: new FormControl<string | null>(null),
      lossComment: new FormControl<string | null>(null),
      archivedAt: new FormControl<Date | null>(null),
      archivedBy: new FormControl<BusinessUser | null>(null),
      statusAt: new FormControl<Date | null>(null),
      statusBy: new FormControl<BusinessUser | null>(null),
      reminderCount: new FormControl<number | null>(null),
      nextReminderDate: new FormControl<string | null>(null),
      brand: new FormControl<string | null>(null),
      logoFileId: new FormControl<string | null>(null),
      annexes: new FormArray<ContractAnnexForm>([]),
      isLastSignatureExpired: new FormControl<boolean | null>(null),
      nextReminderDateModifiedAt: new FormControl<Date | null>(null),
      nextReminderDateModifiedBy: new FormControl<BusinessUser | null>(null)
    });
  }

  fromContract(contract: Contract): void {
    this.patchValue({
      id: contract.id,
      startDate: contract.startDate,
      internalComment: contract.internalComment,
      contractStatus: contract.status,
      company: contract.company,
      businessUnit: contract.businessUnit,
      contact: contract.contact,
      prospect: contract.prospect,
      sentAt: contract.sentAt,
      signedAt: contract.signedAt,
      sentBy: contract.sentBy,
      signedBy: contract.signedBy,
      lostAt: contract.lostAt,
      lostBy: contract.lostBy,
      lossReason: contract.lossReason,
      lossComment: contract.lossComment,
      archivedAt: contract.archivedAt,
      archivedBy: contract.archivedBy,
      statusAt: contract.statusAt,
      statusBy: contract.statusBy,
      owner: contract.salesRepresentativeUser,
      reminderCount: contract.reminderCount,
      nextReminderDate: contract.nextReminderDate,
      brand: contract.brand,
      logoFileId: contract.logoFileId,
      isLastSignatureExpired: contract.isLastSignatureExpired,
      nextReminderDateModifiedAt: contract.nextReminderDateModifiedAt,
      nextReminderDateModifiedBy: contract.nextReminderDateModifiedBy
    });

    this.prestations.clear();

    contract.prestations.forEach(prestation => this.addPrestation(prestation));

    this.files.clear();

    contract.files.forEach(file => this.addContractFile(file));

    this.filesToDelete.clear();

    this.annexes.clear();

    contract.annexes.forEach((annex: ContractAnnex) => this.addAnnex(annex));
  }

  toContract(): Contract {
    const contract = new Contract();

    contract.id = this.id.value;
    contract.startDate = this.startDate.value ? DateHelper.format(this.startDate.value) : null;
    contract.internalComment = this.internalComment.value;
    contract.status = this.contractStatus.value;
    contract.company = this.company.value;
    contract.businessUnit = this.businessUnit.value;
    contract.prestations = this.prestations.controls.map((prestationForm: PrestationForm) =>
      prestationForm.toPrestation()
    );
    contract.salesRepresentativeUser = this.owner.value;
    contract.contact = this.contact.value;
    contract.files = this.getContractFiles();
    contract.modelOfferFiles = this.getModelOfferFiles();
    contract.prospect = this.prospect.value;
    contract.sentAt = this.sentAt.value;
    contract.signedAt = this.signedAt.value;
    contract.sentBy = this.sentBy.value;
    contract.signedBy = this.signedBy.value;
    contract.reminderCount = this.reminderCount.value;
    contract.nextReminderDate = this.nextReminderDate.value;
    contract.nextReminderDateModifiedAt = this.nextReminderDateModifiedAt.value;
    contract.nextReminderDateModifiedBy = this.nextReminderDateModifiedBy.value;
    contract.brand = this.brand.value;
    contract.logoFileId = this.logoFileId.value;
    contract.annexes = this.annexes.controls.map((annexForm: ContractAnnexForm) => annexForm.toContractAnnex());

    return contract;
  }

  removePrestation(index: number): void {
    this.prestations.removeAt(index);
  }

  removeModelOffer(modelOfferId: number): void {
    const prestationsToKeep = this.prestations.controls.filter(
      (prestationForm: PrestationForm) => prestationForm.contractOffer.value?.offer?.id !== modelOfferId
    );
    const annexesToKeep = this.annexes.controls.filter(
      (annexForm: ContractAnnexForm) => annexForm.contractOffer.value?.offer?.id !== modelOfferId
    );
    const filesToKeep = this.files.controls.filter(
      (fileForm: GIFileForm) => fileForm.contractOffer.value?.offer?.id !== modelOfferId
    );
    const filesToDelete = this.files.controls.filter(
      (fileForm: GIFileForm) => fileForm.contractOffer.value?.offer?.id === modelOfferId
    );

    this.prestations.controls = [...prestationsToKeep];
    this.annexes.controls = [...annexesToKeep];
    this.files.controls = [...filesToKeep];
    this.filesToDelete.controls = [...this.filesToDelete.controls, ...filesToDelete];
  }

  addContractFile(file: GIFile): void {
    const fileForm = new GIFileForm();
    fileForm.fromFile(file);
    this.files.push(fileForm);
  }

  addAnnex(annex: ContractAnnex): void {
    const annexForm = new ContractAnnexForm();
    annexForm.fromContractAnnex(annex);
    annexForm.disableTypeAndLabelFromLines();
    this.annexes.push(annexForm);
  }

  removeAnnex(index: number): void {
    this.annexes.removeAt(index);
  }

  getContractFiles(): GIFile[] {
    return this.files.controls
      .filter(
        (fileForm: GIFileForm) =>
          fileForm.id.value && (!fileForm.contractOffer.value || fileForm.contractOffer.value?.id)
      )
      .map((fileForm: GIFileForm) => fileForm.toFile());
  }

  getModelOfferFiles(): GIFile[] {
    return this.files.controls
      .filter(
        (fileForm: GIFileForm) => fileForm.id.value && fileForm.contractOffer.value && !fileForm.contractOffer.value?.id
      )
      .map((fileForm: GIFileForm) => fileForm.toFile());
  }

  disableTasksFromOffers(): void {
    this.prestations.controls.forEach((prestationForm: PrestationForm) => {
      if (prestationForm.contractOffer?.value?.id) {
        prestationForm.disableTask();
      }
    });
  }

  disableTypeAndLabelFromAnnexLines(): void {
    this.annexes.controls.forEach((annexForm: ContractAnnexForm) => annexForm.disableTypeAndLabelFromLines());
  }

  incrementReminderCount(): void {
    const reminderCount = this.reminderCount.value;
    this.reminderCount.patchValue(reminderCount + 1);
  }

  addAnnexesFromModelOffer(modelOffer: ModelOffer): void {
    modelOffer.annexes.forEach((annex: ModelOfferContractAnnexModel) => {
      if (annex.model.archivedAt) {
        return;
      }
      const contractAnnex: ContractAnnex = ContractAnnex.fromContractAnnexModel(annex.model);
      contractAnnex.offer = ContractOffer.fromModelOffer(modelOffer);
      this.addAnnex(contractAnnex);
    });
    this.annexes.controls = [...this.annexes.controls];
  }
}
